import type { MutableRefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setTypeaheadUserDatesInteracted, setTypeaheadUserSelectedDates, setTypeaheadUserSelectedOccupancy } from 'actions/SearchActions'
import { mapOccupanciesToTypeaheadFormat } from 'lib/search/searchUtils'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { rem } from 'polished'
import styled from 'styled-components'
import AIGlowBorder from 'components/Search/SearchForm/AIGlowBorder'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import {
  formatDate,
  getOccupancyDescription,
} from 'components/SearchV2/Utils/GlobalSearchV2'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import TravellersFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/TravellersFloatingPanel'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/LocationFloatingPanel'
import SearchTrendingDestinations from 'components/Search/SearchForm/SearchTrendingDestinations'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import { DATE_SEARCH_OPTION_IDS, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import useGlobalSearchURLHashVertical, { DATE_SELECT_OPTION } from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import { EmptyArray } from 'lib/array/arrayUtils'
import { NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS } from 'constants/naturalLanguageSearch'
import AIGlowHighlightContext from 'contexts/aiGlowHighlightContext'
import useAIConfirmationGlow from 'hooks/useAIConfirmationGlow'
import { acknowledgeDateConfirmation } from 'lib/search/typeaheadDateConfirmationUtils'
import GlobalSearchLoyaltyBurnToggle from 'components/Search/GlobalSearch/GlobalSearchLoyaltyBurnToggle'
import noop from 'lib/function/noop'
import type { CalendarFocusMonth } from 'components/Common/Calendar/calendarFocusMonth'

/* Extended AIGlowBorder with white background and no border for children */
const StyledAIGlowBorder = styled(AIGlowBorder)`
  > * {
    background: white;
    border: none !important;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
      margin-top: ${rem(8)};
    }
  }
`

const StyledTrendingDestinations = styled(SearchTrendingDestinations)`
  margin-top: ${rem(24)};
`

const ModalToggleWrapper = styled.div`
  padding-top: ${rem(16)};
`

enum SearchStep {
  LOCATION = 'location',
  DATE = 'date',
  GUESTS = 'guests',
}

interface Props {
  closeSearch?: () => void
  onSearch: () => void
  isInPage?: boolean // i.e. not part of drawer
  maxGuests?: number
  focusMonth?: CalendarFocusMonth
  updateSearchSessionId?: (newSessionId: string) => void
  temporarySearchSubmitRef?: MutableRefObject<App.TypeaheadSubmitRefData | null>
}

function HotelsTabContent(props: Props) {
  const {
    closeSearch = noop,
    onSearch,
    isInPage,
    maxGuests,
    focusMonth,
    updateSearchSessionId,
    temporarySearchSubmitRef,
  } = props
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const locationLabels = useLocationSearchLabels()

  const {
    searchItem,
    checkinDate,
    checkoutDate,
    occupancies = EmptyArray,
    areRoomErrorsShown,
    flexibleMonths,
    flexibleNights,
    dateSearchOptionId,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const { openedSearchVertical, option } = useGlobalSearchURLHashVertical()

  const isDateSelectOpen = openedSearchVertical === 'hotels' && option === DATE_SELECT_OPTION

  const recentSearches = useRecentSearches()

  const DATE_INPUT_PLACEHOLDER = intl.formatMessage({
    id: 'yTj1l4rcgPlSgXhzjDOF',
    defaultMessage: 'Anytime',
    description: 'Placeholder text for date input when no date is selected',
  })

  const stepsValidation = useMemo(
    () => ({
      [SearchStep.LOCATION]: searchItem,
      [SearchStep.DATE]:
        (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME || (checkinDate && checkoutDate) || flexibleMonths),
    }),
    [searchItem, dateSearchOptionId, checkinDate, checkoutDate, flexibleMonths],
  )

  const flexibleSearchSelectionText =
    prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights, undefined, intl)

  const [activeStep, setActiveStep] = useState<SearchStep>()
  const [glowThisOpening, setGlowThisOpening] = useState(false)
  const { goToNextStep, goToPreviousStep, closeActiveStep, isSearchEnabled } =
    useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const consumePendingDateConfirmation = useCallback(() => {
    const refData = temporarySearchSubmitRef?.current
    if (!refData?.dateConfirmationRequired || refData.dateConfirmationViewed) {
      return false
    }

    acknowledgeDateConfirmation(refData)
    refData.dateConfirmationRequired = false
    return true
  }, [temporarySearchSubmitRef])

  const openLocationSearchStep = useCallback(
    () => {
      setHasUserInteracted(prev => ({ ...prev, location: false }))
      setActiveStep(SearchStep.LOCATION)
    },
    [],
  )
  const openDateSearchStep = useCallback(() => {
    if (consumePendingDateConfirmation()) {
      setGlowThisOpening(true)
    }
    setActiveStep(SearchStep.DATE)
  }, [consumePendingDateConfirmation])

  const closeDatePanel = useCallback(() => {
    setGlowThisOpening(false)
    if (isDateSelectOpen) closeSearch()
    closeActiveStep()
  }, [closeActiveStep, closeSearch, isDateSelectOpen])

  const confirmDatePanel = useCallback(() => {
    setGlowThisOpening(false)
    goToNextStep()
  }, [goToNextStep])

  const goBackFromDatePanel = useCallback(() => {
    setGlowThisOpening(false)
    goToPreviousStep()
  }, [goToPreviousStep])

  const openGuestsSearchStep = useCallback(
    () => setActiveStep(SearchStep.GUESTS),
    [],
  )

  useEffect(() => {
    if (isDateSelectOpen) {
      openDateSearchStep()
    }
  }, [isDateSelectOpen, openDateSearchStep])

  const { checkin, checkout } = useMemo(
    () => ({
      checkin: formatDate(checkinDate),
      checkout: formatDate(checkoutDate),
    }),
    [checkinDate, checkoutDate],
  )

  const guestsDescription = useMemo(
    () => getOccupancyDescription(occupancies, intl),
    [occupancies, intl],
  )
  const dateSearchProps = useDateSearchProps({
    onAnytimeDatesSelected: closeActiveStep,
  })

  const locationPanelTitle = useMemo(
    () => intl.formatMessage({
      id: '_PdNsEJ0XYwldrsZcJ_8',
      defaultMessage: 'Where will you stay?',
      description: 'Title for location search panel in natural language search mode',
    }),
    [intl],
  )

  const searchButtonText = intl.formatMessage({
    id: 'NlUtVRbkXOmpSFkheT4Z',
    defaultMessage: 'Search',
    description: 'Search button text for global hotels search on the hotel page',
  })

  const applyButtonText = intl.formatMessage({
    id: 'ENxPPE_4gjNvtXZpYAUq',
    defaultMessage: 'Apply',
    description: 'Apply button text for search form panels in the search interface',
  })

  const whenLabel = intl.formatMessage({
    id: 'G-va_y_Ri6pCw2Ek4uPR',
    defaultMessage: 'When',
    description: 'Label for the date search field input in the main search and discovery interface',
  })

  const checkInLabel = intl.formatMessage({
    id: 'BZImHfWDmEabHe95A4Ie',
    defaultMessage: 'Check in',
    description: 'Form field label for the hotel check-in date in the booking date picker',
  })

  const checkOutLabel = intl.formatMessage({
    id: 'zNj1m3NcaWN8Inz8iuy9',
    defaultMessage: 'Check out',
    description: 'Label for check-out date field in the date selection or calendar picker',
  })

  const guestsLabel = intl.formatMessage({
    id: 'j_s8RUuhdvQTR8vVHX5u',
    defaultMessage: 'Guests',
    description: 'Label for guests/occupancy field in the occupancy or guest picker',
  })

  const handleDateConfirmationRequired = useCallback(() => {
    if (activeStep !== SearchStep.DATE) {
      return
    }

    if (consumePendingDateConfirmation()) {
      setGlowThisOpening(true)
    }
  }, [activeStep, consumePendingDateConfirmation])

  const onUserSelectedDates = useCallback((dates: App.TypeaheadExtractedTravelDates) => {
    setGlowThisOpening(false)

    const refData = temporarySearchSubmitRef?.current
    if (refData) {
      acknowledgeDateConfirmation(refData)
      refData.dateConfirmationRequired = false
    }

    const typeaheadId = refData?.typeaheadId
    if (!typeaheadId) return

    dispatch(setTypeaheadUserSelectedDates({ typeaheadId, dates }))
  }, [dispatch, temporarySearchSubmitRef])

  const onUserSelectedOccupancy = useCallback((rooms: Array<App.Occupants>) => {
    const typeaheadId = temporarySearchSubmitRef?.current?.typeaheadId
    if (!typeaheadId || !rooms || rooms.length === 0) return

    dispatch(setTypeaheadUserSelectedOccupancy({
      typeaheadId,
      occupancy: mapOccupanciesToTypeaheadFormat(rooms),
    }))
  }, [dispatch, temporarySearchSubmitRef])

  const [hasUserInteracted, setHasUserInteracted] = useState({
    dates: false,
    travelers: false,
    location: false,
  })

  const onUserDatesInteracted = useCallback(() => {
    setHasUserInteracted(prev => ({ ...prev, dates: true }))
    setGlowThisOpening(false)

    const refData = temporarySearchSubmitRef?.current
    if (refData) {
      acknowledgeDateConfirmation(refData)
      refData.dateConfirmationRequired = false
    }

    if (refData?.typeaheadId) {
      dispatch(setTypeaheadUserDatesInteracted({ typeaheadId: refData.typeaheadId }))
    }
  }, [dispatch, temporarySearchSubmitRef])

  const handleUserSelectedLocation = useCallback(() => {
    setHasUserInteracted(prev => ({ ...prev, location: true }))
  }, [])

  const handleLocationConfirm = useCallback(() => {
    openDateSearchStep()
    setHasUserInteracted(prev => ({ ...prev, location: false }))
  }, [openDateSearchStep])

  const handleDateStep = useCallback(() => {
    openDateSearchStep()
  }, [openDateSearchStep])

  const handleGuestsStep = useCallback(() => {
    setHasUserInteracted(prev => ({ ...prev, travelers: true }))
    openGuestsSearchStep()
  }, [openGuestsSearchStep])

  const [travelDatesShowAIGlow, setTravelDatesShowAIGlow] = useState(false)

  useEffect(() => {
    if (travelDatesShowAIGlow) {
      const timer = setTimeout(() => {
        setTravelDatesShowAIGlow(false)
      }, NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS)

      return () => clearTimeout(timer)
    }
  }, [travelDatesShowAIGlow])

  const [occupancyShowAIGlow, setOccupancyShowAIGlow] = useState(false)

  useEffect(() => {
    if (occupancyShowAIGlow) {
      const timer = setTimeout(() => {
        setOccupancyShowAIGlow(false)
      }, NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS)

      return () => clearTimeout(timer)
    }
  }, [occupancyShowAIGlow])

  const isDatePanelOpen = activeStep === SearchStep.DATE
  const isConfirmationGlowing = useAIConfirmationGlow(isDatePanelOpen && glowThisOpening)

  const locationField = (
    <StyledAIGlowBorder>
      <SearchFormField
        label={locationLabels.titleLabelNaturalLanguage}
        placeholder={locationLabels.placeholderLabelNaturalLanguage}
        value={searchItem?.format.mainText}
        onClick={openLocationSearchStep}
      />
    </StyledAIGlowBorder>
  )

  return (
    <ContentWrapper>
      <LocationFloatingPanel
        typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
        isOpen={activeStep === SearchStep.LOCATION}
        onClose={closeActiveStep}
        onConfirm={handleLocationConfirm}
        onGoBack={goToPreviousStep}
        typeaheadVertical="hotel"
        recentSearches={recentSearches}
        updateSearchSessionId={updateSearchSessionId}
        placeholder={locationLabels.placeholderLabelNaturalLanguage}
        multiline
        showNaturalLanguageSearchSuggestions
        label={locationLabels.titleLabelNaturalLanguage}
        title={locationPanelTitle}
        userInteractions={hasUserInteracted}
        setTravelDatesShowAIGlow={setTravelDatesShowAIGlow}
        setOccupancyShowAIGlow={setOccupancyShowAIGlow}
        onDateConfirmationRequired={handleDateConfirmationRequired}
        temporarySearchSubmitRef={temporarySearchSubmitRef}
        onUserSelectedLocation={handleUserSelectedLocation}
      />

      <AIGlowHighlightContext.Provider value={isConfirmationGlowing}>
        <DateFloatingPanel
          {...dateSearchProps}
          focusMonth={focusMonth}
          isOpen={isDatePanelOpen}
          onClose={closeDatePanel}
          onConfirm={confirmDatePanel}
          onGoBack={goBackFromDatePanel}
          confirmText={isSearchEnabled ? searchButtonText : applyButtonText}
          onUserDatesChanged={onUserSelectedDates}
          onUserDatesInteracted={onUserDatesInteracted}
          useExactHotelDurations
        />
      </AIGlowHighlightContext.Provider>

      <TravellersFloatingPanel
        isOpen={activeStep === SearchStep.GUESTS}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? searchButtonText : applyButtonText}
        onUserSelectedOccupancy={onUserSelectedOccupancy}
        maxOccupants={maxGuests}
      />

      <FieldsWrapper>
        {locationField}

        <SearchFormFieldGroup>
          {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <SearchFormField
              label={whenLabel}
              onClick={handleDateStep}
              placeholder={DATE_INPUT_PLACEHOLDER}
              value={flexibleSearchSelectionText}
              showAIGlow={travelDatesShowAIGlow}
            />
          )}
          {dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <>
              <SearchFormField
                label={checkInLabel}
                placeholder={DATE_INPUT_PLACEHOLDER}
                value={checkin}
                onClick={handleDateStep}
                showAIGlow={travelDatesShowAIGlow}
              />
              <SearchFormField
                label={checkOutLabel}
                placeholder={DATE_INPUT_PLACEHOLDER}
                value={checkout}
                onClick={handleDateStep}
                showAIGlow={travelDatesShowAIGlow}
              />
            </>
          )}
        </SearchFormFieldGroup>
        <SearchFormField
          label={guestsLabel}
          value={guestsDescription}
          onClick={handleGuestsStep}
          hasError={areRoomErrorsShown}
          showAIGlow={occupancyShowAIGlow}
        />

      </FieldsWrapper>
      {!isInPage &&
        <StyledTrendingDestinations
          titleColour="light-foreground-alt"
          onClick={closeSearch}
        />}
      {!isInPage && <ModalToggleWrapper>
        <GlobalSearchLoyaltyBurnToggle />
      </ModalToggleWrapper>}
    </ContentWrapper>
  )
}

export default HotelsTabContent
